/**
 * Joue un effet sonore court.
 * Gère l'état muet global stocké dans localStorage.
 *
 * @param soundId L'ID de l'élément <audio> HTML à jouer.
 * @param volume Le volume de lecture (0.0 à 1.0). Défaut à 0.5.
 */
export function playSoundEffect(soundId: string, volume: number = 0.5): void {
    const isMuted = localStorage.getItem('genesisTrail_menuSoundMuted') === 'true';

    if (isMuted) {
        // console.log(`[Audio SFX] Son ${soundId} ignoré (muet).`); // Optionnel: log si muet
        return;
    }

    const audioElement = document.getElementById(soundId) as HTMLAudioElement | null;

    if (audioElement && typeof audioElement.play === 'function') {
        // S'assurer que le volume est dans les limites
        audioElement.volume = Math.max(0, Math.min(1, volume));
        // Rembobiner au début pour les sons courts répétitifs
        audioElement.currentTime = 0;

        audioElement.play().catch(error => {
            console.warn(`[Audio SFX] Erreur lors de la lecture du son ${soundId}:`, error);
            // Gérer les erreurs de lecture (par exemple, interaction utilisateur requise)
        });
    } else {
        console.warn(`[Audio SFX] Élément audio non trouvé ou invalide pour ID: ${soundId}`);
    }
}

/**
 * Contrôle la lecture/pause d'un élément audio (pour musiques de fond).
 * Gère l'état muet global.
 * Important: Pour démarrer la lecture initiale, assurez-vous que l'utilisateur a interagi avec la page.
 *
 * @param soundId L'ID de l'élément <audio> HTML à contrôler.
 * @param action 'play' ou 'pause'.
 * @param volume Volume à appliquer lors de la lecture (0.0 à 1.0). Défaut à 0.3.
 * @param loop Indique si la musique doit boucler. Défaut à true.
 * @returns Promise<void> qui se résout quand la lecture démarre (si action play) ou immédiatement (si pause).
 */
export async function controlBackgroundMusic(soundId: string, action: 'play' | 'pause', volume: number = 0.3, loop: boolean = true): Promise<void> {
    const isMuted = localStorage.getItem('genesisTrail_menuSoundMuted') === 'true';
    const audioElement = document.getElementById(soundId) as HTMLAudioElement | null;

    if (!audioElement) {
        console.warn(`[Audio BGM] Élément audio non trouvé pour ID: ${soundId}`);
        return Promise.resolve(); // Retourner une promesse résolue
    }

    audioElement.loop = loop; // Définir la boucle

    if (action === 'pause') {
        if (!audioElement.paused) {
            audioElement.pause();
            console.log(`[Audio BGM] Musique ${soundId} mise en pause.`);
        }
        return Promise.resolve();
    }

    // Action 'play'
    if (isMuted) {
        console.log(`[Audio BGM] Lecture de ${soundId} ignorée (muet).`);
        // S'assurer qu'elle est en pause si muet
        if (!audioElement.paused) audioElement.pause();
        return Promise.resolve();
    }

    // Si non muet et action 'play'
    if (audioElement.paused) {
         // Appliquer le volume avant de jouer
         audioElement.volume = Math.max(0, Math.min(1, volume));
         try {
             await audioElement.play();
             console.log(`[Audio BGM] Lecture de ${soundId} démarrée.`);
         } catch (error) {
             console.warn(`[Audio BGM] Erreur lors de la lecture du son ${soundId} (interaction?):`, error);
             // Rejeter la promesse en cas d'erreur
             return Promise.reject(error);
         }
    } else {
         // Déjà en lecture, ajuster le volume si nécessaire (ex: unmute)
         audioElement.volume = Math.max(0, Math.min(1, volume));
         return Promise.resolve(); // Déjà en lecture, succès immédiat
    }
} 